import React, { useEffect, useState } from 'react';
import { get } from '../utils/apiClient';
import { getCachedData, setCachedData, isCacheStale } from '../utils/cacheUtils';
import LoadingSpinner from '../components/LoadingSpinner';
import PageHeader from '../components/PageHeader';
import {
  DonatePageContainer,
  Section,
  SectionTitle,
  TextBlock,
  CampaignsGrid,
  CampaignCard,
  CampaignTitle,
  CampaignDescription,
  ImpactGrid,
  ImpactCard,
  ImpactTitle,
  ImpactDescription,
} from '../styles/DonatePageStyles';

const CACHE_KEY = 'donate_data';
const MAX_AGE_MS = 1000 * 60 * 60;

const DonatePage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const cached = await getCachedData(CACHE_KEY);

      if (cached && !isCacheStale(cached, MAX_AGE_MS)) {
        if (!isMounted) return;
        setPageData(cached.data);
        setLoading(false);
      }

      try {
        const data = await get('/donate');
        if (!isMounted) return;
        setPageData(data);
        setCachedData(CACHE_KEY, data);
        setLoading(false);
      } catch (err) {
        if (!isMounted) return;
        if (!cached) {
          setLoading(false);
        }
        setError(err && err.message ? err.message : 'Failed to load donate page.');
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading && !pageData) {
    return (
      <DonatePageContainer>
        <LoadingSpinner />
      </DonatePageContainer>
    );
  }

  if (error && !pageData) {
    return <DonatePageContainer>{error}</DonatePageContainer>;
  }

  const header = pageData && pageData.header ? pageData.header : null;
  const purposeText = pageData ? pageData.purposeText : null;
  const buildingProjectAppeal = pageData ? pageData.buildingProjectAppeal : null;
  const campaigns = pageData && Array.isArray(pageData.campaigns) ? pageData.campaigns : [];
  const impactMessages =
    pageData && Array.isArray(pageData.impactMessages) ? pageData.impactMessages : [];

  return (
    <DonatePageContainer>
      {header && (
        <PageHeader
          title={header.title}
          image={header.backgroundImage}
        />
      )}

      {purposeText && (
        <Section>
          <SectionTitle>Why Give?</SectionTitle>
          <TextBlock>{purposeText}</TextBlock>
        </Section>
      )}

      {buildingProjectAppeal && (buildingProjectAppeal.title || buildingProjectAppeal.text) && (
        <Section>
          <SectionTitle>{buildingProjectAppeal.title || 'Building Project Appeal'}</SectionTitle>
          {buildingProjectAppeal.text && (
            <TextBlock>{buildingProjectAppeal.text}</TextBlock>
          )}
        </Section>
      )}

      {campaigns.length > 0 && (
        <Section>
          <SectionTitle>Current Campaigns</SectionTitle>
          <CampaignsGrid>
            {campaigns.map((campaign, index) => (
              <CampaignCard key={campaign._id || campaign.title || index}>
                {campaign.title && <CampaignTitle>{campaign.title}</CampaignTitle>}
                {campaign.description && (
                  <CampaignDescription>{campaign.description}</CampaignDescription>
                )}
              </CampaignCard>
            ))}
          </CampaignsGrid>
        </Section>
      )}

      {impactMessages.length > 0 && (
        <Section>
          <SectionTitle>Impact of Your Giving</SectionTitle>
          <ImpactGrid>
            {impactMessages.map((impact, index) => (
              <ImpactCard key={impact._id || impact.title || index}>
                {impact.title && <ImpactTitle>{impact.title}</ImpactTitle>}
                {impact.description && (
                  <ImpactDescription>{impact.description}</ImpactDescription>
                )}
              </ImpactCard>
            ))}
          </ImpactGrid>
        </Section>
      )}
    </DonatePageContainer>
  );
};

export default DonatePage;
